import { useEffect, useMemo, useState } from 'react'

import MetricCard from './MetricCard'
import { useToast } from '../contexts/ToastContext'
import { traceService } from '../services/traceService'
import {
  catalogSyncService,
  type CatalogCategorySnapshot,
  type CatalogChangeEntry,
  type CatalogProductSnapshot,
  type CatalogSnapshotEntry,
  type CatalogSyncView,
} from '../services/catalogSyncService'

type TenantOption = {
  id: string
  name: string
  slug: string
}

const statusTone: Record<string, { bg: string; border: string; color: string; label: string }> = {
  SYNCED: { bg: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.30)', color: '#bbf7d0', label: 'Synced' },
  PARTIAL: { bg: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.32)', color: '#fde68a', label: 'Partial' },
  ERROR: { bg: 'rgba(239,68,68,0.14)', border: '1px solid rgba(239,68,68,0.35)', color: '#fecaca', label: 'Error' },
  SYNCING: { bg: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.30)', color: '#bfdbfe', label: 'Syncing' },
  PENDING: { bg: 'rgba(168,85,247,0.12)', border: '1px solid rgba(168,85,247,0.30)', color: '#e9d5ff', label: 'Pending' },
  IDLE: { bg: 'rgba(148,163,184,0.10)', border: '1px solid rgba(148,163,184,0.24)', color: '#cbd5e1', label: 'Idle' },
}

function formatDate(value: string | null | undefined) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function formatNumber(value: number | null | undefined) {
  const n = Number(value ?? 0)
  if (!Number.isFinite(n)) return '-'
  return n.toLocaleString('es-ES')
}

function formatCurrency(value: number, currency: string) {
  try {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency }).format(value / 100)
  } catch {
    return `${formatNumber(value)} ${currency}`
  }
}

function isProductSnapshot(payload: CatalogProductSnapshot | CatalogCategorySnapshot): payload is CatalogProductSnapshot {
  return 'sku' in payload
}

function hasPreviousPayload(
  item: CatalogSnapshotEntry | CatalogChangeEntry
): item is CatalogChangeEntry & { previousPayload: CatalogProductSnapshot | CatalogCategorySnapshot } {
  return 'previousPayload' in item && Boolean(item.previousPayload)
}

function payloadLabel(payload: CatalogProductSnapshot | CatalogCategorySnapshot) {
  return isProductSnapshot(payload) ? `${payload.name} | ${payload.sku}` : `${payload.name} | ${payload.slug}`
}

export function getProductRelationSummary(payload: CatalogProductSnapshot): string {
  const firstClassProductCount = payload.firstClassProductRelations?.length ?? 0
  const productCount = payload.complementProductRefs.length + firstClassProductCount
  const categoryCount = payload.complementCategoryRefs.length
  const relationCount = productCount + categoryCount
  if (relationCount === 0) return ''
  return ` | complements ${productCount} prod / ${categoryCount} cat`
}

function getCategoryRelationSummary(payload: CatalogCategorySnapshot): string {
  const relationCount = payload.firstClassCategoryRelations?.length ?? 0
  if (relationCount === 0) return ''
  return ` | relations ${relationCount} cat`
}

function payloadSubtitle(payload: CatalogProductSnapshot | CatalogCategorySnapshot) {
  if (!isProductSnapshot(payload)) {
    const role = payload.commercialRole ? ` | role ${payload.commercialRole}` : ''
    return `${payload.isActive ? 'active' : 'inactive'} | order ${payload.sortOrder} | parent ${payload.parentSlug || '-'}${role}${getCategoryRelationSummary(payload)}`
  }

  const role = payload.commercialRole ?? payload.categoryCommercialRole
  const roleSuffix = role ? ` | role ${role}` : ''
  return `${formatCurrency(payload.priceCents, payload.currency)} | stock ${payload.stock} | ${payload.isFeatured ? 'featured' : 'normal'}${roleSuffix}${getProductRelationSummary(payload)}`
}

function statusBadge(status: string | null | undefined) {
  const resolved = String(status || 'IDLE').toUpperCase()
  return statusTone[resolved] || statusTone.IDLE
}

function entityLabel(entry: CatalogChangeEntry | CatalogSnapshotEntry) {
  return payloadLabel(entry.payload)
}

function entitySubtitle(entry: CatalogChangeEntry | CatalogSnapshotEntry) {
  return payloadSubtitle(entry.payload)
}

function renderDiffChip(label: string, count: number) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 8px',
        borderRadius: 999,
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.10)',
        color: '#cbd5e1',
        fontSize: 11,
        fontWeight: 700,
      }}
    >
      <span>{label}</span>
      <span style={{ color: '#93c5fd' }}>{count}</span>
    </span>
  )
}

function DiffList({
  title,
  items,
  kind,
}: {
  title: string
  items: Array<CatalogSnapshotEntry | CatalogChangeEntry>
  kind: 'added' | 'updated' | 'removed'
}) {
  const tone =
    kind === 'added'
      ? { bg: 'rgba(34,197,94,0.06)', border: 'rgba(34,197,94,0.18)', color: '#bbf7d0' }
      : kind === 'updated'
        ? { bg: 'rgba(59,130,246,0.06)', border: 'rgba(59,130,246,0.18)', color: '#bfdbfe' }
        : { bg: 'rgba(239,68,68,0.06)', border: 'rgba(239,68,68,0.18)', color: '#fecaca' }

  return (
    <div style={{ flex: 1, minWidth: 220, borderRadius: 14, border: `1px solid ${tone.border}`, background: tone.bg, padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 900, color: tone.color }}>{title}</div>
        {renderDiffChip(kind, items.length)}
      </div>

      {items.length === 0 ? (
        <div style={{ fontSize: 12, color: 'rgba(226,232,240,0.55)' }}>Sin cambios</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((item) => (
            <div
              key={`${kind}-${item.entityType}-${item.entityId}`}
              style={{
                borderRadius: 12,
                border: '1px solid rgba(255,255,255,0.10)',
                background: 'rgba(255,255,255,0.03)',
                padding: 10,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 800, color: '#e2e8f0' }}>
                {kind === 'updated' && hasPreviousPayload(item) ? (
                  <>
                    {payloadLabel(item.previousPayload)} {'->'} {entityLabel(item)}
                  </>
                ) : (
                  entityLabel(item)
                )}
              </div>
              <div style={{ marginTop: 4, fontSize: 11, color: 'rgba(226,232,240,0.66)', lineHeight: 1.45 }}>
                {kind === 'updated' && hasPreviousPayload(item) ? (
                  <>
                    {payloadSubtitle(item.previousPayload)}
                    <br />
                    {'-> '}
                    {entitySubtitle(item)}
                  </>
                ) : (
                  entitySubtitle(item)
                )}
              </div>
              <div
                style={{
                  marginTop: 6,
                  fontSize: 10,
                  color: 'rgba(226,232,240,0.45)',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                }}
              >
                {item.entityType}:{item.entityId}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function CatalogSyncCard({ view }: { view: CatalogSyncView }) {
  const tone = statusBadge(view.state?.status)
  const state = view.state

  return (
    <section
      style={{
        borderRadius: 18,
        border: '1px solid rgba(255,255,255,0.10)',
        background: 'linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01))',
        padding: 16,
      }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 15, fontWeight: 900 }}>{view.target.instanceName}</div>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '4px 8px',
                borderRadius: 999,
                background: tone.bg,
                border: tone.border,
                color: tone.color,
                fontSize: 11,
                fontWeight: 800,
              }}
            >
              {tone.label}
            </span>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '4px 8px',
                borderRadius: 999,
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.10)',
                color: '#cbd5e1',
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {view.target.channelType}
            </span>
          </div>
          <div style={{ marginTop: 6, fontSize: 12, color: 'rgba(226,232,240,0.60)' }}>{view.target.channelInstanceId}</div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <div style={{ fontSize: 11, color: 'rgba(226,232,240,0.58)' }}>Last sync: {formatDate(state?.lastSyncedAt)}</div>
          <div style={{ fontSize: 11, color: 'rgba(226,232,240,0.58)' }}>Last job: {state?.lastJobId || '-'}</div>
          <div
            style={{
              fontSize: 10,
              color: 'rgba(226,232,240,0.42)',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            }}
          >
            hash={view.summary.snapshotHash}
          </div>
        </div>
      </div>

      {state?.lastError ? (
        <div
          style={{
            marginTop: 12,
            padding: 10,
            borderRadius: 12,
            border: '1px solid rgba(239,68,68,0.30)',
            background: 'rgba(239,68,68,0.08)',
            color: '#fecaca',
            fontSize: 12,
          }}
        >
          {state.lastError}
        </div>
      ) : null}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10, marginTop: 14 }}>
        <MetricCard title="Products" value={view.summary.productCount} icon="P" />
        <MetricCard title="Categories" value={view.summary.categoryCount} icon="C" />
        <MetricCard title="Featured" value={view.summary.featuredCount} icon="*" type="success" />
        <MetricCard title="Pending" value={view.diff.totalPending} icon="~" type={view.diff.totalPending > 0 ? 'warning' : 'success'} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12, marginTop: 14 }}>
        <div
          style={{
            borderRadius: 14,
            border: '1px solid rgba(255,255,255,0.10)',
            background: 'rgba(255,255,255,0.02)',
            padding: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 900 }}>Estado</div>
            <div style={{ fontSize: 11, color: 'rgba(226,232,240,0.58)' }}>Updated: {formatDate(state?.updatedAt)}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, fontSize: 12, lineHeight: 1.5 }}>
            <div>
              <div style={{ color: 'rgba(226,232,240,0.55)' }}>Last attempt</div>
              <div>{formatDate(state?.lastAttemptAt)}</div>
            </div>
            <div>
              <div style={{ color: 'rgba(226,232,240,0.55)' }}>Last synced</div>
              <div>{formatDate(state?.lastSyncedAt)}</div>
            </div>
            <div>
              <div style={{ color: 'rgba(226,232,240,0.55)' }}>Products</div>
              <div>{formatNumber(state?.productCount)}</div>
            </div>
            <div>
              <div style={{ color: 'rgba(226,232,240,0.55)' }}>Categories</div>
              <div>{formatNumber(state?.categoryCount)}</div>
            </div>
          </div>
        </div>

        <div
          style={{
            borderRadius: 14,
            border: '1px solid rgba(255,255,255,0.10)',
            background: 'rgba(255,255,255,0.02)',
            padding: 12,
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 10 }}>Diff summary</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {renderDiffChip('Products added', view.diff.products.counts.added)}
            {renderDiffChip('Products updated', view.diff.products.counts.updated)}
            {renderDiffChip('Products removed', view.diff.products.counts.removed)}
            {renderDiffChip('Categories added', view.diff.categories.counts.added)}
            {renderDiffChip('Categories updated', view.diff.categories.counts.updated)}
            {renderDiffChip('Categories removed', view.diff.categories.counts.removed)}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 10 }}>Products diff</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <DiffList title="Added" items={view.diff.products.added} kind="added" />
            <DiffList title="Updated" items={view.diff.products.updated} kind="updated" />
            <DiffList title="Removed" items={view.diff.products.removed} kind="removed" />
          </div>
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 10 }}>Categories diff</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <DiffList title="Added" items={view.diff.categories.added} kind="added" />
            <DiffList title="Updated" items={view.diff.categories.updated} kind="updated" />
            <DiffList title="Removed" items={view.diff.categories.removed} kind="removed" />
          </div>
        </div>
      </div>
    </section>
  )
}

export default function CatalogSyncPage() {
  const { addToast } = useToast()
  const [tenants, setTenants] = useState<TenantOption[]>([])
  const [tenantId, setTenantId] = useState('')
  const [selectedInstanceId, setSelectedInstanceId] = useState('all')
  const [sampleLimit, setSampleLimit] = useState('10')
  const [views, setViews] = useState<CatalogSyncView[]>([])
  const [loadingTenants, setLoadingTenants] = useState(true)
  const [loadingViews, setLoadingViews] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    const loadTenants = async () => {
      setLoadingTenants(true)
      try {
        const response = await traceService.getTenants()
        if (cancelled) return
        const list = response.data || []
        setTenants(list)
        setTenantId((current) => current || list[0]?.id || '')
      } catch {
        if (!cancelled) {
          setError('No se pudieron cargar los tenants')
        }
      } finally {
        if (!cancelled) setLoadingTenants(false)
      }
    }

    void loadTenants()
    return () => {
      cancelled = true
    }
  }, [])

  const loadViews = async () => {
    if (!tenantId) {
      setViews([])
      setLoadingViews(false)
      return
    }

    setLoadingViews(true)
    setError('')
    try {
      const data = await catalogSyncService.getViews({
        tenantId,
        sampleLimit: Number(sampleLimit) || 10,
      })
      setViews(data)
    } catch {
      setViews([])
      setError('No se pudo cargar el estado del catalog sync')
    } finally {
      setLoadingViews(false)
    }
  }

  useEffect(() => {
    void loadViews()
  }, [tenantId, sampleLimit])

  useEffect(() => {
    if (selectedInstanceId === 'all') return
    if (views.length === 0) return
    if (!views.some((view) => view.target.channelInstanceId === selectedInstanceId)) {
      setSelectedInstanceId('all')
    }
  }, [selectedInstanceId, views])

  const visibleViews = useMemo(() => {
    if (selectedInstanceId === 'all') return views
    return views.filter((view) => view.target.channelInstanceId === selectedInstanceId)
  }, [selectedInstanceId, views])

  const selectedView = visibleViews[0] || views[0] || null

  const stats = useMemo(() => {
    const effective = selectedView
    const errorCount = views.filter((view) => {
      const status = String(view.state?.status || '').toUpperCase()
      return status === 'ERROR' || status === 'PARTIAL'
    }).length

    return {
      productCount: effective?.summary.productCount ?? 0,
      categoryCount: effective?.summary.categoryCount ?? 0,
      featuredCount: effective?.summary.featuredCount ?? 0,
      pendingCount: effective?.diff.totalPending ?? 0,
      instanceCount: views.length,
      errorCount,
    }
  }, [selectedView, views])

  const handleRefresh = async () => {
    if (!tenantId) return

    setRefreshing(true)
    setError('')
    try {
      await catalogSyncService.refresh({
        tenantId,
        channelInstanceId: selectedInstanceId === 'all' ? undefined : selectedInstanceId,
        force: true,
      })

      addToast({
        type: 'success',
        title: 'Sync queued',
        message: 'El refresh de catalogo quedo en cola',
      })
    } catch {
      addToast({
        type: 'error',
        title: 'Error',
        message: 'No se pudo encolar el refresh de catalogo',
      })
    } finally {
      setRefreshing(false)
      void loadViews()
    }
  }

  const tenantOptions = tenants.map((tenant) => ({
    value: tenant.id,
    label: tenant.name || tenant.slug || tenant.id,
  }))

  const instanceOptions = useMemo(() => {
    const options = visibleViews.length > 0 ? visibleViews : views
    return [{ value: 'all', label: 'Todas las instancias' }, ...options.map((view) => ({
      value: view.target.channelInstanceId,
      label: `${view.target.instanceName} | ${view.target.channelType}`,
    }))]
  }, [visibleViews, views])

  return (
    <div className="ai-providers-page">
      <div className="page-header">
        <div>
          <h2>Catalog sync</h2>
          <p className="page-subtitle">Estado, diff y sincronizacion por tenant e instancia de canal.</p>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={() => void loadViews()} disabled={!tenantId || loadingViews || refreshing}>
            Recargar estado
          </button>
          <button className="btn btn-primary" onClick={() => void handleRefresh()} disabled={!tenantId || loadingViews || refreshing}>
            {refreshing ? 'Encolando...' : 'Encolar sync'}
          </button>
        </div>
      </div>

      <div className="filters-bar">
        <div className="filters-group">
          <div className="filter-item">
            <label>Tenant</label>
            {loadingTenants ? (
              <input value="Cargando tenants..." disabled />
            ) : (
              <select value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
                <option value="">Selecciona un tenant</option>
                {tenantOptions.map((tenant) => (
                  <option key={tenant.value} value={tenant.value}>
                    {tenant.label}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="filter-item">
            <label>Instancia</label>
            <select value={selectedInstanceId} onChange={(e) => setSelectedInstanceId(e.target.value)}>
              {instanceOptions.map((instance) => (
                <option key={instance.value} value={instance.value}>
                  {instance.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-item">
            <label>Muestras</label>
            <select value={sampleLimit} onChange={(e) => setSampleLimit(e.target.value)}>
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
          </div>
        </div>
      </div>

      <div className="summary-cards">
        <MetricCard title="Products" value={stats.productCount} icon="P" />
        <MetricCard title="Categories" value={stats.categoryCount} icon="C" />
        <MetricCard title="Featured" value={stats.featuredCount} icon="*" type="success" />
        <MetricCard title="Pending" value={stats.pendingCount} icon="~" type={stats.pendingCount > 0 ? 'warning' : 'success'} />
        <MetricCard title="Instances" value={stats.instanceCount} icon="I" />
        <MetricCard title="Issues" value={stats.errorCount} icon="!" type={stats.errorCount > 0 ? 'error' : 'success'} />
      </div>

      {!tenantId ? (
        <div className="empty-state" style={{ marginTop: 18 }}>
          <div className="empty-icon">tenant</div>
          <h3>Selecciona un tenant</h3>
          <p>El estado y diff del catalogo aparecen por tenant e instancia.</p>
        </div>
      ) : loadingViews ? (
        <div className="loading-container" style={{ marginTop: 18 }}>
          <div className="spinner" />
          <p>Cargando estado del catalogo...</p>
        </div>
      ) : error ? (
        <div style={{ marginTop: 18, padding: 14, borderRadius: 14, border: '1px solid rgba(239,68,68,0.35)', background: 'rgba(239,68,68,0.08)', color: '#fecaca' }}>
          {error}
        </div>
      ) : visibleViews.length === 0 ? (
        <div className="empty-state" style={{ marginTop: 18 }}>
          <div className="empty-icon">sync</div>
          <h3>No hay instancias para sincronizar</h3>
          <p>Conecta una instancia de WhatsApp para ver el estado y el diff del catalogo.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 18 }}>
          {visibleViews.map((view) => (
            <CatalogSyncCard key={view.target.channelInstanceId} view={view} />
          ))}
        </div>
      )}
    </div>
  )
}
